// =================================================================
// Store: Levels slice (PSD §9.1.4 / TODO Task 2.7)
// =================================================================
import type { StateCreator } from 'zustand';
import { ConfidenceStatus, PKSource, type PKParameters } from '@/types';
import { sawchukZaske, correctLevelForDrawTime, bayesianPosterior } from '@/engine';
import type { StoreState, LevelsSlice } from './types';

export const createLevelsSlice: StateCreator<StoreState, [], [], LevelsSlice> = (set, get) => ({
  levels: [],
  pkParameters: null,
  hartfordLevel: null,

  addLevel: (level) => {
    set((state) => ({ levels: [...state.levels, level] }));
    get().scheduleComputeAll();
  },

  removeLevel: (id) => {
    set((state) => ({ levels: state.levels.filter((l) => l.id !== id) }));
    get().scheduleComputeAll();
  },

  setHartfordLevel: (level) => {
    set({ hartfordLevel: level });
    get().scheduleComputeAll();
  },

  runSawchukZaske: () => {
    const state = get();
    const usable = state.levels.slice().sort(
      (a, b) => a.drawOffsetFromInfusionEndMin - b.drawOffsetFromInfusionEndMin,
    );
    if (usable.length < 2 || !state.regimen) return;
    const infusionHr = state.strategyConfig.infusionDurationMin / 60;
    const doseMg = state.regimen.empiricDoseMg || 0;
    const sz = sawchukZaske(usable[0], usable[1], doseMg, infusionHr, state.dosingWeight?.selectedWeightKg ?? 70);
    if (!sz.ok) {
      set({ lastEngineError: sz.error });
      return;
    }
    const pkParameters: PKParameters = {
      source: PKSource.PatientSpecific,
      kelPerHr: sz.value.kelPerHr,
      halfLifeHr: sz.value.halfLifeHr,
      vdLkg: sz.value.vdLkg,
      vdL: sz.value.vdL,
      method: 'sawchuk-zaske',
      confidence: sz.value.lowConfidence ? ConfidenceStatus.RequiresReview : ConfidenceStatus.LevelInformed,
    };
    set({ pkParameters, lastEngineError: null });
    get().computeAll();
  },

  applyDrawTimeCorrection: (levelId) => {
    const state = get();
    const level = state.levels.find((l) => l.id === levelId);
    const kel = state.pkParameters?.kelPerHr ?? state.renal?.populationKelPerHr ?? 0.05;
    if (!level) return;
    const targetOffset = 30; // peak target post-infusion
    const corrected = correctLevelForDrawTime(
      level.valueMg,
      level.drawOffsetFromInfusionEndMin,
      targetOffset,
      kel,
    );
    if (!corrected.ok) {
      set({ lastEngineError: corrected.error });
      return;
    }
    set((s) => ({
      levels: s.levels.map((l) =>
        l.id === levelId ? { ...l, correctedValueMg: corrected.value.correctedValue } : l,
      ),
    }));
  },

  runBayesian: () => {
    const state = get();
    if (!state.renal || !state.dosingWeight) return;
    const prior = {
      kelPrior: state.renal.populationKelPerHr,
      kelVariance: 0.01,
      vdPrior: state.agentParams.vdLkg,
      vdVariance: 0.05,
    };
    const result = bayesianPosterior(
      prior,
      state.levels,
      state.regimen?.empiricDoseMg ?? 0,
      state.strategyConfig.infusionDurationMin / 60,
      state.dosingWeight.selectedWeightKg,
    );
    if (!result.ok) {
      set({ lastEngineError: result.error });
      return;
    }
    const pkParameters: PKParameters = {
      source: PKSource.PatientSpecific,
      kelPerHr: result.value.kelPerHr,
      halfLifeHr: Math.log(2) / result.value.kelPerHr,
      vdLkg: result.value.vdL / state.dosingWeight.selectedWeightKg,
      vdL: result.value.vdL,
      method: 'bayesian',
      confidence: result.value.confidence,
    };
    set({ pkParameters });
    get().computeAll();
  },

  setPKFromPrior: (prior) => {
    if (!prior.priorKel || !prior.priorVd) return;
    const dw = get().dosingWeight?.selectedWeightKg ?? 70;
    const pkParameters: PKParameters = {
      source: PKSource.PatientSpecific,
      kelPerHr: prior.priorKel,
      halfLifeHr: Math.log(2) / prior.priorKel,
      vdLkg: prior.priorVd,
      vdL: prior.priorVd * dw,
      method: 'prior-exposure',
      confidence: ConfidenceStatus.LevelInformed,
    };
    set({ pkParameters });
    get().computeAll();
  },
});
